import React, { useState, useEffect } from 'react';
import {
  IconPlayerPlay,
  IconUsers,
  IconWorld,
  IconArrowRight
} from '@tabler/icons-react';
import { tunnelsAPI } from '../../services/api';
import { GameIcon, GAMES } from '../../constants/games';
import ServersTab from './tabs/ServersTab';
import StatusTab from './tabs/StatusTab';

const HeroSection = () => {
  const [currentGame, setCurrentGame] = useState(0);
  const [currentServerIndex, setCurrentServerIndex] = useState(0);
  const [tunnels, setTunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('servers');
  
  // Популярные игры для ротации в заголовке
  const rotatingGames = ['Minecraft', 'Terraria', 'Counter-Strike 2', 'Hytale'];

  // Загрузить туннели при монтировании
  useEffect(() => {
    const fetchTunnels = async () => {
      try {
        const data = await tunnelsAPI.list();
        setTunnels(data || []);
        if (data && data.length > 0) {
          setCurrentServerIndex(Math.floor(Math.random() * data.length));
        }
      } catch (error) {
        console.log('Туннели не загружены (возможно, не авторизован)', error);
        setTunnels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTunnels();
  }, []);

  // Ротация названия игры
  useEffect(() => {
    const gameInterval = setInterval(() => {
      setCurrentGame((prev) => (prev + 1) % rotatingGames.length);
    }, 2500);
    return () => clearInterval(gameInterval);
  }, [rotatingGames.length]);

  // Ротация туннелей каждые 2 секунды (только если есть туннели)
  useEffect(() => {
    if (tunnels.length === 0) return;

    const serverInterval = setInterval(() => {
      setCurrentServerIndex(Math.floor(Math.random() * tunnels.length));
    }, 2000);
    return () => clearInterval(serverInterval);
  }, [tunnels.length]);

  const currentTunnel = tunnels.length > 0 ? tunnels[currentServerIndex] : null;
  
  // Сформировать адрес подключения
  const getConnectionAddress = () => {
    if (!currentTunnel) return 'Туннели не созданы';
    
    // Приоритет: publicAddress → public_address → publicHost + port
    if (currentTunnel.publicAddress) return currentTunnel.publicAddress;
    if (currentTunnel.public_address) return currentTunnel.public_address;
    
    // Если есть публичный хост и порт, формируем адрес
    const host = currentTunnel.publicHost || currentTunnel.public_host || currentTunnel.hostPort?.split(':')[0] || 'connect.xneon.org';
    const port = currentTunnel.publicPort || currentTunnel.public_port || currentTunnel.hostPort?.split(':')[1];
    
    if (port) return `${host}:${port}`;
    
    // Если есть хост, но нет порта — показываем хост
    if (host && host !== 'connecting...') return host;
    
    return 'connecting...';
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-white dark:bg-dark-950 transition-colors">
      {/* Градиентный фон */}
      <div className="absolute inset-0 bg-white dark:bg-dark-950" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse,rgba(249,115,22,0.15)_0%,transparent_70%)] pointer-events-none" />
      
      {/* Основной контент */}
      <div className="relative z-10 flex-1 flex items-center justify-center pt-20 pb-32">
        <div className="max-w-[1200px] mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          
          {/* Левая часть - текст */}
          <div className="text-center lg:text-left">
            {/* Бейдж */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-dark-850 border border-gray-300 dark:border-dark-800 rounded-full mb-6">
              <span className="bg-primary-500 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">NEW</span>
              <span className="text-gray-700 dark:text-dark-400 text-[13px]">Бесплатно 10 туннелей</span>
            </div>

            {/* Заголовок */}
            <h1 className="text-[32px] sm:text-[40px] lg:text-[52px] font-bold leading-[1.1] mb-6 tracking-[-1px]">
              <span className="text-gray-900 dark:text-white">Играй с друзьями</span>
              <br />
              <span className="text-gray-900 dark:text-white">в </span>
              <span className="bg-gradient-to-r from-primary-500 to-yellow-400 bg-clip-text text-transparent">
                {rotatingGames[currentGame]}
              </span>
              <br />
              <span className="text-gray-900 dark:text-white">мгновенно</span>
            </h1>

            {/* Описание */}
            <p className="text-gray-700 dark:text-dark-500 text-lg mb-8 max-w-[480px] mx-auto lg:mx-0">
              Забудь про VPN, проброс портов и сложные настройки. 
              Подключайся к серверам друзей за 30 секунд.
            </p>

            {/* Кнопки */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-8">
              <a
                href="/download"
                className="w-full sm:w-auto px-5 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2"
              >
                Скачать бесплатно <IconArrowRight className="w-4 h-4" />
              </a>
              
              <a
                href="#how-it-works"
                className="w-full sm:w-auto px-5 py-3 bg-gray-200 dark:bg-dark-850 border border-gray-300 dark:border-dark-800 rounded-lg font-medium text-gray-900 dark:text-dark-200 hover:bg-gray-300 dark:hover:bg-dark-800 hover:border-gray-400 dark:hover:border-dark-700 transition-colors flex items-center justify-center gap-2"
              >
                <IconPlayerPlay className="w-4 h-4" />
                Смотреть демо
              </a>
            </div>
          </div>

          {/* Правая часть - визуал */}
          <div className="w-full">
            <div className="bg-gray-50 dark:bg-dark-900 border border-gray-300 dark:border-dark-800 rounded-2xl overflow-hidden">
              {/* Заголовок карточки с новыми табами */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-300 dark:border-dark-800">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setActiveTab('servers')}
                    className={`px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                      activeTab === 'servers'
                        ? 'bg-gray-300 dark:bg-dark-800 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-dark-500 hover:text-gray-900 dark:hover:text-dark-400'
                    }`}
                  >
                    Туннели
                  </button>
                  <button
                    onClick={() => setActiveTab('status')}
                    className={`px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                      activeTab === 'status'
                        ? 'bg-gray-300 dark:bg-dark-800 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-dark-500 hover:text-gray-900 dark:hover:text-dark-400'
                    }`}
                  >
                    Статус
                  </button>
                </div>
              </div>

              {/* Содержимое табов */}
              <div className="p-5 max-h-[600px] overflow-y-auto bg-white dark:bg-dark-950 transition-colors duration-300">
                {activeTab === 'servers' && <ServersTab />}
                {activeTab === 'status' && <StatusTab />}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
